import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { categories, mainSiteUrl } from '@/mocks/categories';

export default function Navbar() {
  const { t } = useTranslation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-background-50 shadow-sm' : 'bg-transparent'}`}>
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="text-lg font-heading font-semibold text-foreground-900 whitespace-nowrap hover:text-primary-500 transition-colors">
            yoybuyfinds<span className="text-primary-500">.mom</span>
          </Link>

          <div className="hidden md:flex items-center gap-3">
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-2 px-4 py-2 rounded-full border border-foreground-200 text-sm text-foreground-700 bg-background-50/80 hover:border-primary-400 hover:text-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                <span>{t('nav.selectCategory')}</span>
                <span className={`w-4 h-4 flex items-center justify-center transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}>
                  <i className="ri-arrow-down-s-line text-xs"></i>
                </span>
              </button>

              {dropdownOpen && (
                <div className="absolute right-0 mt-2 w-64 bg-background-50 border border-background-200 rounded-lg shadow-lg py-1 max-h-80 overflow-y-auto z-50">
                  {categories.map((cat) => (
                    <a
                      key={cat.nameEn}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                      onClick={() => setDropdownOpen(false)}
                    >
                      {cat.name} <span className="text-foreground-400 text-xs ml-1">({cat.nameEn})</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2 rounded-full bg-foreground-900 text-background-50 text-sm font-medium hover:bg-foreground-800 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              {t('nav.yoybuySheet')}
            </a>

            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              {t('nav.buyItNow')}
            </a>
          </div>

          <div className="flex md:hidden items-center gap-2">
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="px-3 py-1.5 rounded-full bg-foreground-900 text-background-50 text-xs font-medium whitespace-nowrap cursor-pointer"
            >
              {t('nav.yoybuySheet')}
            </a>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="w-9 h-9 flex items-center justify-center rounded-full border border-foreground-200 text-foreground-700 cursor-pointer hover:border-primary-400 transition-colors"
            >
              <i className={`text-lg ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden bg-background-50 border-t border-background-200 py-3 px-4">
            <div className="grid grid-cols-2 gap-1">
              {categories.map((cat) => (
                <a
                  key={cat.nameEn}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="block px-3 py-2 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {cat.name}
                </a>
              ))}
            </div>
            <div className="mt-3 pt-3 border-t border-background-200">
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="block w-full text-center px-4 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                {t('nav.buyItNow')}
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}